import { Outlet } from "react-router-dom";
import { useAlterarPessoa } from "@/api/consultas";
import { recursoInexistente } from "@/api/problema";
import { AbasDaRota } from "@/componentes/AbasDaRota";
import { useAvisos } from "@/componentes/Avisos";
import { Interruptor } from "@/componentes/Interruptor";
import { NaoEncontrado } from "@/componentes/NaoEncontrado";
import { PaginaDeEntidade } from "@/componentes/PaginaDeEntidade";
import { useAlternarSituacao } from "@/componentes/useAlternarSituacao";
import { banco, documento } from "@/formato";
import { ROTA_DOS_CEDENTES, rotaDoCedente } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { ListaDePendencias } from "./ListaDePendencias";
import { marcasDasAbas } from "./pendencias";
import { textoDaRecusa } from "./recusas";
import { bloqueantesDe, SeloDoCedente } from "./SeloDoCedente";
import { useCedenteDaRota } from "./useCedenteDaRota";

/*
 * `/cedentes/:id`: responde "o que falta para esse cedente emitir?".
 *
 * Ordem da spec: cabeçalho, pendências, abas. Os grupos 11, 12 e 13 testam as abas por
 * baixo desta página: nomes das abas, `Outlet` e `key` ficam como estão.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ PRONTIDÃO COM ERRO NA LEITURA É PRONTIDÃO DESCONHECIDA.                  │
 * │                                                                          │
 * │ Depois de uma escrita a consulta refaz a leitura, e se ela falhar o      │
 * │ cache continua com o retrato antigo. Mostrá-lo seria dizer `pronto para  │
 * │ emitir` sobre um cadastro que acabou de mudar: por isso selo, marcas e   │
 * │ lista saem de `prontidao`, que é `undefined` com qualquer erro.          │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * O interruptor fica sem palavra ao lado: `ativo` só repetiria a posição dele, e mentiria
 * com ele desligado. O `aria-label` diz o que ele controla, e o selo, o que foge do normal.
 */
const ABAS = [
  { para: "dados", rotulo: "Dados" },
  { para: "contas", rotulo: "Contas", tambemEm: ["convenios"] },
  { para: "aplicacoes", rotulo: "Aplicações" },
  { para: "boletos", rotulo: "Boletos" },
] as const;

export function PaginaDoCedente() {
  const { id, pessoa, prontidao, consulta } = useCedenteDaRota();
  const { cliente, pode } = useSessao();
  const { feito } = useAvisos();
  const alterar = useAlterarPessoa(cliente, id);

  const nome = pessoa?.razao_social;
  const falhou = consulta.error !== null;
  const marcas = marcasDasAbas(prontidao);

  const situacao = useAlternarSituacao({
    ligado: pessoa?.status === "ativo",
    desligar: {
      titulo: `Bloquear ${nome ?? "cedente"}?`,
      corpo: "A emissão passa a recusar boletos deste cedente.",
      verbo: "Bloquear cedente",
    },
    ligar: {
      titulo: `Reativar ${nome ?? "cedente"}?`,
      corpo: "A emissão volta a aceitar boletos deste cedente.",
      verbo: "Reativar cedente",
    },
    aoAlterar: async (ligar, motivo) => {
      try {
        await alterar.mutateAsync(ligar ? { status: "ativo" } : { status: "bloqueado", motivo });
      } catch (falha) {
        // A confirmação escreve a mensagem como veio, e `detail` diz "beneficiário".
        throw new Error(textoDaRecusa(falha), { cause: falha });
      }
      feito(ligar ? "Cedente reativado" : "Cedente bloqueado");
    },
  });

  if (recursoInexistente(consulta.error)) {
    return (
      <NaoEncontrado
        texto="Cedente não encontrado."
        para={ROTA_DOS_CEDENTES}
        rotuloDoLink="Cedentes"
      />
    );
  }

  return (
    <PaginaDeEntidade
      key={id}
      secao="Cedentes"
      voltarPara={ROTA_DOS_CEDENTES}
      // Sem o nome, `Cedente`: `undefined` diria que ainda está carregando, para sempre.
      titulo={nome ?? (falhou ? "Cedente" : undefined)}
      selo={
        pessoa !== undefined &&
        prontidao !== undefined && (
          <SeloDoCedente
            status={pessoa.status}
            pronto={prontidao.pronto}
            bloqueantes={bloqueantesDe(prontidao)}
          />
        )
      }
      subtitulo={
        pessoa !== undefined &&
        [documento(pessoa.documento), ...pessoa.bancos.map(banco)].join(" · ")
      }
      acoes={
        pessoa !== undefined && pode("admin") ? (
          <Interruptor
            ligado={pessoa.status === "ativo"}
            rotulo="Cedente ativo"
            aoAlternar={situacao.pedir}
          />
        ) : undefined
      }
    >
      <ListaDePendencias pessoaID={id} prontidao={prontidao} falhou={falhou} />

      <AbasDaRota
        base={rotaDoCedente(id)}
        rotulo="Seções do cedente"
        abas={ABAS.map((aba) => ({ ...aba, marca: marcas[aba.para] }))}
      >
        <Outlet />
      </AbasDaRota>

      {situacao.dialogo}
    </PaginaDeEntidade>
  );
}
